import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { COMPANY_NAME, LOGO_URL, PORTAL_LABEL } from '../config/branding';

// ── Step constants ────────────────────────────────────────────────────────────
const STEP_CREDENTIALS = 'credentials';
const STEP_MFA_SETUP   = 'mfa_setup';   // first-time QR enrolment
const STEP_MFA_VERIFY  = 'mfa_verify';  // subsequent logins

export default function Login() {
  const [step, setStep]         = useState(STEP_CREDENTIALS);
  const [form, setForm]         = useState({ email: '', password: '' });
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [signupEnabled, setSignupEnabled] = useState(false);
  useEffect(() => { apiFetch('/auth/signup-enabled').then(d => setSignupEnabled(!!d.enabled)).catch(() => {}); }, []);
  const [showPwd, setShowPwd]   = useState(false);
  // MFA state
  const [tempToken, setTempToken]   = useState('');
  const [qrCode, setQrCode]         = useState('');
  const [mfaSecret, setMfaSecret]   = useState('');
  const [otpDigits, setOtpDigits]   = useState(['','','','','','']);
  const inputRefs = useRef([]);

  const { login } = useAuth();
  const nav = useNavigate();

  function navigateAfterLogin(user) {
    if (user.role === 'admin' || user.role === 'super_admin') nav('/admin');
    else if (user.role === 'manager')     nav('/manager/tickets');
    else if (user.role === 'agent')       nav('/agent/tickets');
    else if (user.role === 'customer_manager') nav('/customer-manager/tickets');
    else nav('/tickets');
  }

  // ── Step 1: credentials ───────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const data = await apiFetch('/auth/login', { method: 'POST', body: form });

      if (data.mfa_setup_required) {
        setTempToken(data.temp_token);
        setQrCode(data.qr_code);
        setMfaSecret(data.secret);
        setStep(STEP_MFA_SETUP);
        return;
      }
      if (data.mfa_required) {
        setTempToken(data.temp_token);
        setStep(STEP_MFA_VERIFY);
        return;
      }
      // Normal login
      login(data.user, data.token, data.audit_id);
      navigateAfterLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── OTP digit input helpers ───────────────────────────────────────────────
  function handleDigit(i, val) {
    const v = val.replace(/\D/g, '').slice(-1);
    const next = [...otpDigits];
    next[i] = v;
    setOtpDigits(next);
    if (v && i < 5) inputRefs.current[i + 1]?.focus();
  }
  function handleDigitKeyDown(i, e) {
    if (e.key === 'Backspace' && !otpDigits[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  }
  function handleDigitPaste(e) {
    const text = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if (text.length === 6) {
      setOtpDigits(text.split(''));
      inputRefs.current[5]?.focus();
      e.preventDefault();
    }
  }
  const otpCode = otpDigits.join('');

  // ── Step 2a: confirm MFA setup ────────────────────────────────────────────
  async function handleSetupVerify(e) {
    e.preventDefault();
    if (otpCode.length < 6) return;
    setError(''); setLoading(true);
    try {
      const data = await apiFetch('/auth/mfa/verify-setup', {
        method: 'POST', body: { temp_token: tempToken, code: otpCode },
      });
      login(data.user, data.token, data.audit_id);
      navigateAfterLogin(data.user);
    } catch (err) {
      setError(err.message);
      setOtpDigits(['','','','','','']);
      inputRefs.current[0]?.focus();
    } finally { setLoading(false); }
  }

  // ── Step 2b: verify MFA on subsequent logins ──────────────────────────────
  async function handleMfaVerify(e) {
    e.preventDefault();
    if (otpCode.length < 6) return;
    setError(''); setLoading(true);
    try {
      const data = await apiFetch('/auth/mfa/verify', {
        method: 'POST', body: { temp_token: tempToken, code: otpCode },
      });
      login(data.user, data.token, data.audit_id);
      navigateAfterLogin(data.user);
    } catch (err) {
      setError(err.message);
      setOtpDigits(['','','','','','']);
      inputRefs.current[0]?.focus();
    } finally { setLoading(false); }
  }

  // ── Shared layout wrapper ─────────────────────────────────────────────────
  const BrandPanel = (
    <div className="login-brand-panel" style={{
      background: 'linear-gradient(155deg, #0f5132 0%, #0B6623 45%, #094d1e 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '60px 56px',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: -80, right: -80, width: 280, height: 280, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
      <div style={{ position: 'absolute', bottom: -100, left: -60, width: 360, height: 360, borderRadius: '50%', background: 'rgba(255,255,255,.04)' }} />
      <div style={{ position: 'absolute', top: '30%', right: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,.06)' }} />
      <div style={{ position: 'relative', textAlign: 'center', maxWidth: 380 }}>
        <div style={{ background: 'rgba(255,255,255,.12)', borderRadius: 24, padding: '24px 32px', display: 'inline-block', marginBottom: 36, backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,.2)' }}>
          <img src={LOGO_URL} alt={COMPANY_NAME} style={{ height: 90, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
        </div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: '#fff', letterSpacing: -.5, marginBottom: 14, lineHeight: 1.2 }}>
          {COMPANY_NAME} {PORTAL_LABEL}
        </h1>
        <p style={{ fontSize: 15, color: 'rgba(255,255,255,.75)', lineHeight: 1.7, marginBottom: 40 }}>
          Manage customer complaints, track SLAs, and resolve issues efficiently — all in one place.
        </p>
        {[
          { icon: '🎫', text: 'Ticket management with SLA tracking' },
          { icon: '💬', text: 'Real-time live chat with customers' },
          { icon: '📊', text: 'Dashboard reports & Excel export' },
          { icon: '🔒', text: 'Role-based access for teams' },
        ].map(f => (
          <div key={f.text} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, textAlign: 'left' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{f.icon}</div>
            <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,.85)', fontWeight: 500 }}>{f.text}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const ErrorBanner = error && (
    <div style={{ background: 'var(--red-50)', border: '1px solid rgba(239,68,68,.3)', borderLeft: '4px solid #ef4444', color: '#b91c1c', padding: '11px 14px', borderRadius: 10, marginBottom: 22, fontSize: 13.5, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span>⚠</span> {error}
    </div>
  );

  // ── OTP box UI ────────────────────────────────────────────────────────────
  const OtpBoxes = (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 28 }} onPaste={handleDigitPaste}>
      {otpDigits.map((d, i) => (
        <input
          key={i}
          ref={el => inputRefs.current[i] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={e => handleDigit(i, e.target.value)}
          onKeyDown={e => handleDigitKeyDown(i, e)}
          style={{
            width: 48, height: 56, textAlign: 'center', fontSize: 24, fontWeight: 800,
            borderRadius: 10, border: `2px solid ${d ? 'var(--status-resolved-text)' : 'var(--border-subtle)'}`,
            background: 'var(--bg-surface)', color: 'var(--text-primary)',
            outline: 'none', transition: 'border-color .15s',
          }}
          autoFocus={i === 0}
        />
      ))}
    </div>
  );

  // ──────────────────────────────────────────────────────────────────────────
  // STEP: MFA SETUP (first time enrolment)
  // ──────────────────────────────────────────────────────────────────────────
  if (step === STEP_MFA_SETUP) return (
    <div className="login-grid" style={{ flex: 1, display: 'grid', background: 'var(--bg-app)', minHeight: 'calc(100vh - 70px)' }}>
      {BrandPanel}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 56px' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🔐</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>Set Up Authenticator</h2>
            <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Your organisation requires two-factor authentication.<br />
              Scan the QR code with Google Authenticator or Authy.
            </p>
          </div>

          {ErrorBanner}

          {/* Steps */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 24 }}>
            {[
              { n:'1', text:'Install Google Authenticator or Authy on your phone' },
              { n:'2', text:'Tap the + button and choose "Scan QR code"' },
              { n:'3', text:'Scan the QR code below, then enter the 6-digit code' },
            ].map(s => (
              <div key={s.n} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--status-resolved-bg)', color: 'var(--status-resolved-text)', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{s.n}</div>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{s.text}</span>
              </div>
            ))}
          </div>

          {/* QR Code */}
          {qrCode && (
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ display: 'inline-block', background: '#fff', padding: 12, borderRadius: 12, border: '1px solid var(--border-subtle)' }}>
                <img src={qrCode} alt="MFA QR Code" style={{ width: 180, height: 180, display: 'block' }} />
              </div>
            </div>
          )}

          {/* Manual entry fallback */}
          {mfaSecret && (
            <details style={{ marginBottom: 20, fontSize: 12 }}>
              <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)', userSelect: 'none' }}>Can't scan? Enter code manually</summary>
              <div style={{ marginTop: 8, background: 'var(--bg-app)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '8px 12px', fontFamily: 'monospace', fontSize: 13, letterSpacing: 2, color: 'var(--text-primary)', wordBreak: 'break-all' }}>
                {mfaSecret}
              </div>
            </details>
          )}

          <form onSubmit={handleSetupVerify}>
            <p style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .5, textAlign: 'center', marginBottom: 14 }}>
              Enter the 6-digit code from your app
            </p>
            {OtpBoxes}
            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%', justifyContent: 'center', borderRadius: 10 }}
              disabled={loading || otpCode.length < 6}
            >
              {loading ? <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Verifying…</> : 'Confirm & Sign In →'}
            </button>
          </form>

          <button onClick={() => { setStep(STEP_CREDENTIALS); setError(''); setOtpDigits(['','','','','','']); }}
            style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>
            ← Back to login
          </button>
        </div>
      </div>
    </div>
  );

  // ──────────────────────────────────────────────────────────────────────────
  // STEP: MFA VERIFY (subsequent logins)
  // ──────────────────────────────────────────────────────────────────────────
  if (step === STEP_MFA_VERIFY) return (
    <div className="login-grid" style={{ flex: 1, display: 'grid', background: 'var(--bg-app)', minHeight: 'calc(100vh - 70px)' }}>
      {BrandPanel}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 56px' }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--status-resolved-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, margin: '0 auto 16px' }}>🔒</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6 }}>Two-Factor Authentication</h2>
            <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Open your authenticator app and enter<br />the 6-digit code for KGreen Support.
            </p>
          </div>

          {ErrorBanner}

          <form onSubmit={handleMfaVerify}>
            {OtpBoxes}
            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%', justifyContent: 'center', borderRadius: 10 }}
              disabled={loading || otpCode.length < 6}
            >
              {loading ? <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Verifying…</> : 'Verify & Sign In →'}
            </button>
          </form>

          <button onClick={() => { setStep(STEP_CREDENTIALS); setError(''); setOtpDigits(['','','','','','']); }}
            style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>
            ← Use a different account
          </button>
        </div>
      </div>
    </div>
  );

  // ──────────────────────────────────────────────────────────────────────────
  // STEP: CREDENTIALS (default)
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div className="login-grid" style={{ flex: 1, display: 'grid', background: 'var(--bg-app)', minHeight: 'calc(100vh - 70px)' }}>
      {BrandPanel}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 56px' }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <div style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-main)', letterSpacing: -.4, marginBottom: 6 }}>Welcome back</h2>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Sign in to your account to continue</p>
          </div>

          {ErrorBanner}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, letterSpacing: '.1px' }}>Email address</label>
              <input className="input" type="email" placeholder="you@company.com"
                value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required style={{ fontSize: 14, padding: '10px 14px' }} />
            </div>

            <div style={{ marginBottom: 26 }}>
              <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, letterSpacing: '.1px' }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input className="input" type={showPwd ? 'text' : 'password'} placeholder="••••••••"
                  value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required style={{ fontSize: 14, padding: '10px 44px 10px 14px' }} />
                <button type="button" onClick={() => setShowPwd(p => !p)}
                  style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: '#94a3b8', padding: 4 }}>
                  {showPwd ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center', borderRadius: 10 }} disabled={loading}>
              {loading ? <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Signing in…</> : 'Sign In →'}
            </button>
          </form>

          {signupEnabled && (
          <p style={{ textAlign: 'center', marginTop: 22, fontSize: 13.5, color: 'var(--text-secondary)' }}>
            Don't have an account?{' '}
            <Link to="/register" style={{ color: 'var(--status-resolved-text)', fontWeight: 700, textDecoration: 'none' }}>Create one</Link>
          </p>
          )}
        </div>
      </div>
    </div>
  );
}
